import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useUserPrint } from '@app/hooks';
import type { SendoutReceiversFilterParams } from '@app/pages/Sendouts/SendoutReceivers/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { usePageContext } from '@app/providers/PageProvider/usePageContext';
import { handleCatchError } from '@app/services';
import { useGetSendoutClientsQuery } from '@app/services/rtkQuery/Sendouts/api';
import { generateSpanWithDataTestId } from '@app/shared/utils';

export const useSendoutReceiversEffects = (
  pageNumber: number,
  rowsPerPage: number,
  setTotal: (value: ((prevState: number) => number) | number) => void
) => {
  const { setTitleData } = usePageContext();
  const { showNotification } = useNotification();

  const [query] = useSearchParams();
  const sendoutId = query.get('id');

  const { activeBranchId } = useUserPrint();

  const [getSendoutClientsParams, setGetSendoutClientsParams] =
    useState<SendoutReceiversFilterParams>({
      SearchTerm: '',
      StatusIds: [],
      ActionIds: [],
    });

  const {
    data: sendoutClients,
    error,
    isLoading: isGetSendoutClientsLoading,
    refetch: refetchSendoutClients,
  } = useGetSendoutClientsQuery(
    {
      SendoutId: +sendoutId!,
      ItemsPerPage: rowsPerPage,
      PageN: pageNumber,
      ActiveBranchId: activeBranchId,
      ...getSendoutClientsParams,
    },
    {
      skip: !sendoutId || !activeBranchId,
    }
  );

  useEffect(() => {
    setTotal(sendoutClients?.TotalItems || 0);
  }, [sendoutClients, setTotal]);

  useEffect(() => {
    if (!sendoutClients || !sendoutClients?.Sendout) return;
    setTitleData(
      generateSpanWithDataTestId(sendoutClients.Sendout, 'sendout-name-page')
    );

    return () => {
      setTitleData('');
    };
  }, [setTitleData, sendoutClients]);

  useEffect(() => {
    if (error) {
      showNotification(handleCatchError(error));
    }
  }, [error, showNotification]);

  return {
    sendoutClients,
    isGetSendoutClientsLoading,
    setGetSendoutClientsParams,
    refetchSendoutClients,
  };
};
